
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { BookOpen, Plus, X, Save, Smile, Meh, Frown } from 'lucide-react';
import { JournalEntry } from '../types/portfolio';
import { cn, logInteraction } from '../lib/utils';

interface TradeJournalProps {
  entries: JournalEntry[];
  onSave?: (entry: any) => void;
}

export function TradeJournal({ entries, onSave }: TradeJournalProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newEntry, setNewEntry] = useState({
    symbol: '',
    strategy: 'Trend Following',
    entryReason: '',
    notes: '',
    emotion: 5
  });

  const handleOpenModal = () => {
    logInteraction('TradeJournal', 'Open New Entry Modal');
    setIsModalOpen(true);
  };

  const handleSaveEntry = () => {
    logInteraction('TradeJournal', 'Save Entry', newEntry);
    setIsModalOpen(false);
    onSave?.(newEntry);
  };

  const handleEmotionSelect = (rating: number) => {
    logInteraction('TradeJournal', 'Select Emotion', { rating });
    setNewEntry(prev => ({ ...prev, emotion: rating }));
  };

  return (
    <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-sm">
      <div className="p-4 border-b border-[var(--card-border)] bg-[var(--card-header-bg)] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen size={16} className="text-[var(--muted-text)]" />
          <h3 className="text-sm font-black text-[var(--app-text)] uppercase tracking-tight italic">Trade Journal</h3>
        </div>
        <button 
          onClick={handleOpenModal}
          className="flex items-center gap-1.5 px-3 py-1 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all shadow-sm shadow-emerald-500/20"
        >
          <Plus size={14} />
          <span className="text-[10px] font-black uppercase tracking-widest">Add Entry</span>
        </button>
      </div>

      <div className="p-4 space-y-4 max-h-[400px] overflow-y-auto scrollbar-hide">
        {entries.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest italic">No journal entries yet</p>
          </div>
        ) : (
          entries.map((entry) => (
            <div key={entry.id} className="p-4 bg-[var(--input-bg)] rounded-xl border border-[var(--input-border)] space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-black text-[var(--app-text)] tracking-tight">{entry.symbol}</span>
                  <span className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest bg-[var(--card-bg)] px-1.5 py-0.5 rounded border border-[var(--card-border)]">
                    {entry.strategy}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {entry.emotion_rating >= 4 ? <Smile size={14} className="text-emerald-500" /> : 
                   entry.emotion_rating >= 3 ? <Meh size={14} className="text-amber-500" /> : 
                   <Frown size={14} className="text-rose-500" />}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest block mb-1">Entry Reason</span>
                  <p className="text-[10px] text-[var(--app-text)] font-medium italic leading-relaxed">{entry.entry_reason}</p>
                </div>
                <div>
                  <span className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest block mb-1">Exit Reason</span>
                  <p className="text-[10px] text-[var(--app-text)] font-medium italic leading-relaxed">{entry.exit_reason}</p>
                </div>
              </div>
              <div className="pt-2 border-t border-[var(--card-border)]">
                <p className="text-[10px] text-[var(--muted-text)] font-medium leading-relaxed italic">"{entry.notes}"</p>
              </div>
            </div>
          ))
        )}
      </div>

      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-md"
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative w-full max-w-lg bg-[var(--card-bg)] rounded-3xl shadow-2xl border border-[var(--card-border)] overflow-hidden"
            >
              <div className="p-6 border-b border-[var(--card-border)] flex items-center justify-between bg-[var(--card-header-bg)]">
                <h2 className="text-xl font-black text-[var(--app-text)] uppercase tracking-tight italic">New Journal Entry</h2>
                <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-[var(--input-bg)] rounded-full transition-colors">
                  <X size={20} className="text-[var(--muted-text)]" />
                </button>
              </div>
              
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Symbol</label>
                    <input 
                      type="text" 
                      placeholder="e.g. NVDA" 
                      value={newEntry.symbol}
                      onChange={(e) => setNewEntry(prev => ({ ...prev, symbol: e.target.value }))}
                      className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2 text-sm font-bold focus:outline-none focus:border-emerald-500 transition-colors" 
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Strategy</label>
                    <select 
                      value={newEntry.strategy}
                      onChange={(e) => setNewEntry(prev => ({ ...prev, strategy: e.target.value }))}
                      className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2 text-sm font-bold focus:outline-none focus:border-emerald-500 transition-colors"
                    >
                      <option>Trend Following</option>
                      <option>Mean Reversion</option>
                      <option>Scalping</option>
                      <option>Swing Trading</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Entry Reason</label>
                  <textarea 
                    rows={2} 
                    placeholder="Why did you enter this trade?" 
                    value={newEntry.entryReason}
                    onChange={(e) => setNewEntry(prev => ({ ...prev, entryReason: e.target.value }))}
                    className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2 text-sm font-medium focus:outline-none focus:border-emerald-500 transition-colors resize-none" 
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Notes</label>
                  <textarea 
                    rows={3} 
                    placeholder="Additional observations..." 
                    value={newEntry.notes}
                    onChange={(e) => setNewEntry(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2 text-sm font-medium focus:outline-none focus:border-emerald-500 transition-colors resize-none" 
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Emotion</span>
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => {
                          if (newEntry.emotion === 1) return;
                          handleEmotionSelect(1);
                        }}
                        className={cn(
                          "p-2 rounded-lg transition-all border",
                          newEntry.emotion === 1 
                            ? "bg-rose-500 text-white border-rose-400 shadow-[0_0_10px_rgba(244,63,94,0.3)]" 
                            : "bg-rose-500/10 text-rose-500 border-transparent hover:bg-rose-500/20"
                        )}
                      >
                        <Frown size={20} />
                      </button>
                      <button 
                        onClick={() => {
                          if (newEntry.emotion === 3) return;
                          handleEmotionSelect(3);
                        }}
                        className={cn(
                          "p-2 rounded-lg transition-all border",
                          newEntry.emotion === 3 
                            ? "bg-amber-500 text-white border-amber-400 shadow-[0_0_10px_rgba(245,158,11,0.3)]" 
                            : "bg-amber-500/10 text-amber-500 border-transparent hover:bg-amber-500/20"
                        )}
                      >
                        <Meh size={20} />
                      </button>
                      <button 
                        onClick={() => {
                          if (newEntry.emotion === 5) return;
                          handleEmotionSelect(5);
                        }}
                        className={cn(
                          "p-2 rounded-lg transition-all border",
                          newEntry.emotion === 5 
                            ? "bg-emerald-500 text-white border-emerald-400 shadow-[0_0_10px_rgba(16,185,129,0.3)]" 
                            : "bg-emerald-500/10 text-emerald-500 border-transparent hover:bg-emerald-500/20"
                        )}
                      >
                        <Smile size={20} />
                      </button>
                    </div>
                  </div>
                  <button 
                    onClick={handleSaveEntry}
                    className="flex items-center gap-2 px-6 py-3 bg-[var(--app-text)] text-[var(--card-bg)] rounded-2xl font-black uppercase italic tracking-widest hover:opacity-90 transition-all shadow-lg"
                  >
                    <Save size={18} />
                    Save Entry
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
